import {Component, Injector, OnInit} from '@angular/core';
import {BaseComponent} from '../../base.component';
import {DataResponse} from '../../../network/service/data.response';
import {ListResponse} from '../../../network/service/list.response';
import {SnackBarUtil} from '../../../util/snack.bar.util';
import {FilterUtil} from '../../../util/filter.util';
import {MatDialog} from '@angular/material/dialog';
import {CommentStatus, OrderStatusHelper} from '../../../enums/order.status';
import {RequestType} from '../../../enums/request.type';

@Component({
  selector: 'app-comments',
  standalone: false,
  templateUrl: './comments.html',
  styleUrl: './comments.scss'
})
export class Comments extends BaseComponent implements OnInit {
  displayedColumns = ['User', 'Comment', 'Rate', 'Status', 'CreateDate', 'Actions'];
  displayDataSources = ['User', 'Comment', 'Rate', 'Status', 'CreateDate', 'Actions'];
  displayColumnTypes = ['string', 'string', 'number', 'string', 'date:HH:mm:ss YYYY/MM/DD', 'string'];
  displayEndPoint = ['', '', '', '', '', ''];
  comments?: any = [];
  allFilters = FilterUtil.CommentsFilters;
  filters: any = {};
  isLoading = false;
  isProcessingComment: string | null = null;
  // Branch selection properties
  branches: any[] = [];
  selectedBranchId?: number;
  isLoadingBranches = false;
  // Reply form properties
  showReplyForm = false;
  replyText = '';
  isSubmittingReply = false;
  // Comment selection properties
  selectedComment: any = null;
  branchId?: string;

  constructor(injector: Injector, private dialog: MatDialog) {
    super(injector);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadBranches();
  }

  override loadOnline(): void {
    super.loadOnline();
    this.serviceApi.getComments(this.tableProperties.offset, this.tableProperties.limit, 'User,Factor,Factor.FactorOrders,Factor.FactorOrders.Order,Factor.FactorOrders.Order.ServicePrice,Factor.FactorOrders.Order.ServicePrice.Service', undefined, this.branchId, this.filters).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.comments = res.data;
        this.tableProperties.pageLength = res.count;
        this.setListeners();
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  override pageChanged(event: any) {
    super.pageChanged(event);
    this.tableProperties.offset = (event.pageIndex);
    this.loadOnline();
  }

  override onSortChanged(event: any) {
    super.onSortChanged(event);
    this.loadOnline();
  }

  onFiltersChanged(filters: any) {
    this.filters = filters;
    this.tableProperties.offset = 0;
    this.loadOnline();
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'AWAITING_APPROVAL':
        return 'در انتظار تایید';
      case 'APPROVED':
        return 'تایید شده';
      case 'REJECTED':
        return 'رد شده';
      default:
        return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'AWAITING_APPROVAL':
        return 'status-waiting';
      case 'APPROVED':
        return 'status-approved';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return '';
    }
  }

  formatPrice(price: string): string {
    return new Intl.NumberFormat('fa-IR').format(parseFloat(price));
  }

  getCommentsByStatus(status: string) {
    if (!this.comments) return [];
    return this.comments.filter((comment: any) => comment.Status === status);
  }

  selectComment(comment: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedComment = comment;
    // Reset reply form when selecting a new comment
    this.showReplyForm = false;
    this.replyText = '';
    this.isSubmittingReply = false;
  }

  approveComment(comment: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    this.isProcessingComment = comment.CommentId;
    this.serviceApi.changeComment(comment.CommentId, {
      Status: CommentStatus.APPROVED
    }).subscribe((res: DataResponse<any>) => {
      this.isProcessingComment = null;
      if (res.success) {
        SnackBarUtil.makeMessage(this, 'نظر با موفقیت تایید شد', true);
        this.loadOnline();
        // Update selected comment if it's the same
        if (this.selectedComment?.CommentId === comment.CommentId) {
          this.selectedComment.Status = CommentStatus.APPROVED;
        }
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!) || 'خطا در تایید نظر', false);
      }
    }, (error) => {
      this.isProcessingComment = null;
      SnackBarUtil.makeMessage(this, 'خطا در ارتباط با سرور', false);
    });
  }

  rejectComment(comment: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    this.isProcessingComment = comment.CommentId;
    this.serviceApi.changeComment(comment.CommentId, {
      Status: CommentStatus.REJECTED
    }).subscribe((res: DataResponse<any>) => {
      this.isProcessingComment = null;
      if (res.success) {
        SnackBarUtil.makeMessage(this, 'نظر با موفقیت رد شد', true);
        this.loadOnline();
        // Update selected comment if it's the same
        if (this.selectedComment?.CommentId === comment.CommentId) {
          this.selectedComment.Status = CommentStatus.REJECTED;
        }
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!) || 'خطا در رد نظر', false);
      }
    }, (error) => {
      this.isProcessingComment = null;
      SnackBarUtil.makeMessage(this, 'خطا در ارتباط با سرور', false);
    });
  }

  getOrderStatusText(status: string): string {
    return OrderStatusHelper.getStatusText(status);
  }

  loadBranches() {
    this.isLoadingBranches = true;
    this.serviceApi.getBranches(undefined, undefined, undefined, RequestType.ALL, undefined).subscribe((res: ListResponse<any>) => {
      this.isLoadingBranches = false;
      if (res.success) {
        this.branches = res.data || [];
        // Always select the first branch if available
        if (this.branches.length > 0) {
          this.onBranchClick(this.branches[0]);
        }
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!) || 'خطا در بارگذاری شعب', false);
      }
    }, (error) => {
      this.isLoadingBranches = false;
      SnackBarUtil.makeMessage(this, 'خطا در ارتباط با سرور', false);
    });
  }

  onBranchClick(branch: any) {
    if (this.selectedBranchId !== branch.BusinessId) {
      this.selectedBranchId = branch.BusinessId;
      this.branchId = branch.BusinessId;
      this.tableProperties.offset = 0;
      this.loadOnline();
    }
  }

  isBranchSelected(branch: any): boolean {
    return this.selectedBranchId === branch.BusinessId;
  }

  // Reply form methods
  showReplyFormToggle() {
    this.showReplyForm = !this.showReplyForm;
    if (!this.showReplyForm) {
      this.replyText = '';
    }
  }

  submitReply() {
    if (!this.replyText.trim()) {
      SnackBarUtil.makeMessage(this, 'لطفاً متن پاسخ را وارد کنید', false);
      return;
    }
    this.isSubmittingReply = true;
    this.serviceApi.changeComment(this.selectedComment.CommentId, {
      Reply: this.replyText,
      Status: CommentStatus.APPROVED
    }).subscribe((res: DataResponse<any>) => {
      this.isSubmittingReply = false;
      if (res.success) {
        SnackBarUtil.makeMessage(this, 'پاسخ با موفقیت ارسال شد', true);
        this.showReplyForm = false;
        this.replyText = '';
        // Refresh comments to get updated data
        this.loadOnline();
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!) || 'خطا در ارسال پاسخ', false);
      }
    }, (error) => {
      this.isSubmittingReply = false;
      SnackBarUtil.makeMessage(this, 'خطا در ارتباط با سرور', false);
    });
  }

  cancelReply() {
    this.showReplyForm = false;
    this.replyText = '';
  }
}
